import type { BuilderTemplateId } from '../types/builder'
import type { SolutionId } from '../types/platform'

export const BUILDER_PATH = '/solutions/custom-workflow-builder'

/**
 * Live, DGPT-branded builder entry point. Empty until it exists — "Start Building"
 * then scrolls to the interactive preview on the page instead.
 */
export const BUILDER_ENTRY_URL = ''
export const START_BUILDING_HREF = BUILDER_ENTRY_URL || '#builder'

export const builderTemplateHref = (id: BuilderTemplateId) => `${BUILDER_PATH}?template=${id}#builder`

/** Starter template each existing solution page opens in the builder. */
export const SOLUTION_BUILDER_TEMPLATE: Record<SolutionId, BuilderTemplateId> = {
  invest: 'vc',
  buy: 'procurement',
  acquire: 'ma',
  select: 'grants',
  assure: 'compliance',
  monitor: 'compliance',
}

export const BUILDER_SUGGESTED_PROMPTS: { label: string; prompt: string }[] = [
  { label: 'Build an investment committee workflow.', prompt: 'Build an investment committee workflow.' },
  { label: 'Create a private equity diligence process.', prompt: 'Create a private equity diligence process.' },
  { label: 'Design an M&A evaluation application.', prompt: 'Design an M&A evaluation application.' },
  { label: 'Automate supplier qualification.', prompt: 'Automate supplier qualification.' },
  { label: 'Build a grant review system.', prompt: 'Build a grant review system.' },
  { label: 'Create a custom compliance assessment.', prompt: 'Create a custom compliance assessment.' },
]

/** Model names shown in the builder's selector. Illustrative — availability not confirmed. */
export const BUILDER_MODELS = ['Claude Code', 'OpenAI Codex', 'Mistral', 'Kimi'] as const

export const STARTER_TEMPLATES: { id: BuilderTemplateId; name: string; description: string }[] = [
  { id: 'vc', name: 'Venture Capital', description: 'Investment sourcing, thesis qualification, investment committee, portfolio monitoring.' },
  { id: 'pe', name: 'Private Equity', description: 'Target assessment, financial diligence, commercial evaluation, portfolio analysis.' },
  { id: 'ma', name: 'Acquisitions & M&A', description: 'Target screening, data room analysis, contractual review, integration risk evaluation.' },
  { id: 'procurement', name: 'Procurement', description: 'Supplier qualification, RFP scoring, compliance review, vendor monitoring.' },
  { id: 'grants', name: 'Grants & Funding', description: 'Application intake, eligibility scoring, reviewer assignments, funding reports.' },
  { id: 'blank', name: 'Blank Workspace', description: 'Start from an empty canvas and build a custom diligence system.' },
]

export type ArchitectureLayer = {
  id: string
  name: string
  role: string
  description: string
  items: string[]
  note?: string
}

export const ARCHITECTURE_LAYERS: ArchitectureLayer[] = [
  {
    id: 'governance',
    name: 'Governance Layer',
    role: 'Controls every run',
    description: 'Rules, approvals and records that apply whether a step is done by a person or an agent.',
    items: ['Deterministic business rules', 'Human approval checkpoints', 'Permission management', 'Evidence tracking', 'Audit trails', 'Regulatory framework mapping', 'Secure data handling'],
  },
  {
    id: 'intelligence',
    name: 'DGPT Intelligence Layer',
    role: 'Diligence agents · run inside your application',
    description: 'Specialized DiligenceGPT™ agents that do the diligence work each time the workflow runs.',
    items: ['Data extraction and classification', 'Research and enrichment', 'Risk assessments', 'Financial analysis', 'Benchmarking', 'Due diligence frameworks', 'Report generation', 'Continuous monitoring'],
  },
  {
    id: 'building',
    name: 'DGPT Application Building Layer',
    role: 'Coding agents · build your application',
    description: 'Conversational tools that design and generate the application itself: screens, workflows and integrations.',
    items: ['Conversational application creation', 'Custom interface generation', 'Workflow development', 'AI-assisted coding', 'Custom dashboards and reporting', 'Integration configuration'],
  },
  {
    id: 'frontier',
    name: 'Frontier AI Layer',
    role: 'Underlying models',
    description: 'Leading AI development tools and models that the layers above can draw on.',
    items: ['OpenAI Codex', 'Anthropic Claude Code', 'Mistral', 'Kimi', 'Other supported models'],
    note: 'Model names show intended support, not confirmed live integrations. Ask our team which models are available for your deployment.',
  },
]

export const AGENT_LIBRARY: { id: string; name: string; description: string; agents: string[] }[] = [
  { id: 'collect', name: 'Collect', description: 'Bring information into the workflow.', agents: ['Forms', 'Documents', 'Emails', 'Data rooms', 'External data connections'] },
  { id: 'protect', name: 'Protect', description: 'Control what is seen and by whom.', agents: ['Encryption', 'PII redaction', 'Anonymization', 'Access controls'] },
  { id: 'understand', name: 'Understand', description: 'Turn documents into structured records.', agents: ['Extraction', 'Classification', 'Enrichment', 'Entity resolution'] },
  { id: 'investigate', name: 'Investigate', description: 'Research what matters to the decision.', agents: ['Market intelligence', 'Financial analysis', 'Company research', 'Risk identification'] },
  { id: 'evaluate', name: 'Evaluate', description: 'Measure against your criteria.', agents: ['Qualification', 'Fit scoring', 'Benchmarking', 'Policy assessment'] },
  { id: 'coordinate', name: 'Coordinate', description: 'Keep people and steps moving.', agents: ['Stakeholder emails', 'Information requests', 'Review assignments', 'Approval flows'] },
  { id: 'document', name: 'Document', description: 'Produce the outputs your team uses.', agents: ['Memos', 'Risk reports', 'Scorecards', 'Custom reports'] },
  { id: 'monitor', name: 'Monitor', description: 'Keep watching after the decision.', agents: ['Portfolio changes', 'Supplier performance', 'Document expiry', 'Compliance milestones'] },
]

export const GOVERNANCE_MODULES = [
  'Regulatory framework configuration',
  'Institution-specific diligence policies',
  'Deterministic decision rules',
  'Human-in-the-loop approvals',
  'Evidence-based audit trails',
  'Secure document and data management',
  'Role-based permissions',
  'Workflow and model version history',
  'Ongoing risk monitoring',
]

export type ChainNature = 'probabilistic' | 'evidence' | 'deterministic' | 'human' | 'record'

export const GOVERNANCE_CHAIN: { label: string; nature: ChainNature; example: string; explanation: string }[] = [
  {
    label: 'AI Finding',
    nature: 'probabilistic',
    example: 'Supplier’s current ratio appears to be 0.8.',
    explanation: 'Model-based analysis. Useful and fast, but probabilistic — it must be checked against evidence.',
  },
  {
    label: 'Source Evidence',
    nature: 'evidence',
    example: 'FY2025 balance sheet, page 4.',
    explanation: 'Each finding links to the document and location it came from, so a reviewer can verify it.',
  },
  {
    label: 'Policy Requirement',
    nature: 'deterministic',
    example: 'Policy P-12: current ratio must be ≥ 1.0 for critical suppliers.',
    explanation: 'Your approved criteria, configured once and applied the same way every time.',
  },
  {
    label: 'Deterministic Check',
    nature: 'deterministic',
    example: '0.8 < 1.0 → requirement not met → escalate.',
    explanation: 'A fixed rule, not a model. The same inputs always produce the same result.',
  },
  {
    label: 'Human Approval',
    nature: 'human',
    example: 'Head of Procurement reviews and approves with conditions.',
    explanation: 'A named person makes the consequential decision.',
  },
  {
    label: 'Decision Record',
    nature: 'record',
    example: 'Approved with conditions · rule P-12 · approver · date.',
    explanation: 'The finding, evidence, rule, approver and outcome are stored together for later review.',
  },
]

export const FOUNDATION_FEATURES = [
  'Workspaces',
  'Application forms',
  'Deal pipelines',
  'Document collection',
  'Data rooms',
  'AI analysis',
  'Evaluation frameworks',
  'Collaboration',
  'Reporting',
  'Monitoring',
]

/** Before (DGPT VC workspace) → after (procurement evaluation platform). */
export const WORKSPACE_TRANSFORM: { before: string; after: string }[] = [
  { before: 'Deal pipeline', after: 'Supplier pipeline' },
  { before: 'Pitch deck application form', after: 'Supplier onboarding form' },
  { before: 'Thesis fit score', after: 'Requirements fit score' },
  { before: 'Founder information requests', after: 'Supplier document requests' },
  { before: 'Investment committee', after: 'Procurement approval' },
  { before: 'IC memo', after: 'Approval recommendation' },
  { before: 'Portfolio monitoring', after: 'Vendor monitoring' },
]

export const DEPLOYMENT_STEPS: { label: string; detail: string }[] = [
  { label: 'Design', detail: 'Describe the process and shape the application with the builder.' },
  { label: 'Configure', detail: 'Set criteria, rules, roles and document templates.' },
  { label: 'Connect', detail: 'Connect the data sources your process relies on.' },
  { label: 'Test', detail: 'Run sample cases and validate outputs against expectations.' },
  { label: 'Approve', detail: 'Owners sign off on the workflow, rules and model configuration.' },
  { label: 'Deploy', detail: 'Release to your organization’s workspace.' },
  { label: 'Monitor', detail: 'Review runs, audit views and changes over time.' },
]

export const DEPLOYMENT_CONFIG = [
  'Custom domains and branding',
  'Organization workspaces',
  'Permissions',
  'Data source controls',
  'Model configuration',
  'Policy and approval settings',
  'Testing and validation',
  'Monitoring and audit views',
]

/** Architectural concepts discussed per engagement — not statements of availability. */
export const DEPLOYMENT_OPTIONS: { name: string; description: string }[] = [
  { name: 'Managed workspace', description: 'Your application runs in a dedicated DiligenceGPT™ workspace.' },
  { name: 'Dedicated environment', description: 'An isolated environment for organizations with stricter separation needs.' },
  { name: 'Your infrastructure', description: 'Deployment into infrastructure you control, scoped with our team.' },
]
